/**
 * Zod Schemas for Document Domain Types
 *
 * Provides runtime validation schemas for DocId, DocSnapshot, DocMeta,
 * and CRUD API request/response types.
 *
 * @module schemas/doc.zod
 * @see SPEC-002 Requirements 1.5
 * @see SPEC-004 Requirements 8.1, 8.2, 8.3, 8.4
 */
import { z } from 'zod';
/**
 * Schema for DocId - document identifier with string constraints.
 * Must be a non-empty string with max length of 64 characters.
 */
export const DocIdSchema = z.string().min(1).max(64);
/**
 * Schema for DocSnapshot - complete document state.
 * Validates all required fields for a document snapshot.
 */
export const DocSnapshotSchema = z.object({
    docId: DocIdSchema,
    markdown: z.string(),
    versionId: z.string().min(1),
    createdAtMs: z.number().int().positive(),
    updatedAtMs: z.number().int().positive(),
});
/**
 * Schema for DocMeta - document metadata.
 * Validates metadata fields with optional tags array.
 */
export const DocMetaSchema = z.object({
    docId: DocIdSchema,
    title: z.string().min(1).max(256),
    ownerId: z.string().min(1),
    createdAtMs: z.number().int().positive(),
    updatedAtMs: z.number().int().positive(),
    tags: z.array(z.string()).optional(),
});
// ============================================================================
// SPEC-004: Document CRUD API Schemas
// ============================================================================
/**
 * Schema for ACL structure used in API responses.
 * Simplified ACL format for document metadata.
 */
export const DocACLResponseSchema = z.object({
    owner: z.string().min(1),
    readers: z.array(z.string()),
    writers: z.array(z.string()),
});
/**
 * Schema for Create Document request payload.
 * @see SPEC-004 Requirement 8.1
 */
export const CreateDocRequestSchema = z.object({
    title: z.string().max(200).optional(),
    initialContent: z.string().optional(),
});
/**
 * Schema for Create Document response.
 * @see SPEC-004 Requirement 8.1
 */
export const CreateDocResponseSchema = z.object({
    docId: z.string().uuid(),
});
/**
 * Schema for Update Document request payload.
 * @see SPEC-004 Requirement 8.2
 */
export const UpdateDocRequestSchema = z.object({
    markdown: z.string(),
    meta: z
        .object({
        title: z.string().max(200).optional(),
    })
        .optional(),
});
/**
 * Schema for Update Document response.
 * @see SPEC-004 Requirement 8.2
 */
export const UpdateDocResponseSchema = z.object({
    versionId: z.string().uuid(),
});
/**
 * Schema for Get Document response.
 * @see SPEC-004 Requirement 8.3
 */
export const GetDocResponseSchema = z.object({
    snapshot: z.object({
        markdown: z.string(),
    }),
    meta: z.object({
        docId: z.string().uuid(),
        title: z.string(),
        ownerId: z.string().uuid(),
        createdAt: z.number().int().positive(),
        updatedAt: z.number().int().positive(),
        acl: DocACLResponseSchema,
    }),
    versionId: z.string().uuid(),
});
/**
 * Schema for individual version entry.
 * @see SPEC-004 Requirement 8.4
 */
export const DocVersionSchema = z.object({
    versionId: z.string().uuid(),
    updatedAt: z.number().int().positive(),
});
/**
 * Schema for Get Versions response.
 * @see SPEC-004 Requirement 8.4
 */
export const GetVersionsResponseSchema = z.object({
    versions: z.array(DocVersionSchema),
});
//# sourceMappingURL=doc.zod.js.map